import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { supabase } from '../lib/supabaseClient';
import { SITE_LOGO_URL } from '../lib/brand';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

export default function Registro() {
  const { inviteCode } = useParams();
  const { RUBROS, registerMember } = useApp();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitState, setSubmitState] = useState('form'); // 'form', 'pending_success', 'approved_success'
  const [inviter, setInviter] = useState(null);
  const [capitulos, setCapitulos] = useState([]);

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    capitulo_id: '',
    rubro: RUBROS[0] || 'Tecnología',
    ofrezco: '',
    busco: '',
    foto_url: '',
    whatsapp_number: '',
    whatsapp_visible: true,
    conexion_argentina: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: rpcError } = await supabase.rpc('list_active_capitulos');
      if (cancelled) return;
      if (rpcError) {
        console.error('list_active_capitulos failed:', rpcError);
        setCapitulos([]);
        return;
      }
      const rows = data || [];
      setCapitulos(rows);
      setFormData((prev) => {
        if (rows.some((c) => c.id === prev.capitulo_id)) return prev;
        return { ...prev, capitulo_id: rows[0]?.id || '' };
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!inviteCode) {
      setInviter(null);
      return undefined;
    }

    (async () => {
      const { data, error: rpcError } = await supabase.rpc('lookup_invite_code', {
        p_code: inviteCode.trim(),
      });
      if (cancelled) return;
      if (rpcError) {
        console.error('lookup_invite_code failed:', rpcError);
        setInviter(null);
        return;
      }
      const row = Array.isArray(data) ? data[0] : data;
      setInviter(row?.id ? row : null);
    })();

    return () => {
      cancelled = true;
    };
  }, [inviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const status = await registerMember(formData, inviteCode);
      if (status === 'approved') {
        setSubmitState('approved_success');
      } else {
        setSubmitState('pending_success');
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err.message || 'No se pudo completar el registro');
    } finally {
      setIsLoading(false);
    }
  };

  // View: Pending Approval
  if (submitState === 'pending_success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#FAF9F6] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none sol-watermark" style={{backgroundSize: '600px'}}></div>
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-[40px] shadow-2xl p-10 md:p-14 max-w-lg w-full text-center border border-stone-100 relative z-10"
        >
          <div className="w-24 h-24 bg-[#F6B40E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon name="Clock" className="w-10 h-10 text-[#F6B40E]" />
          </div>
          <h2 className="text-3xl font-editorial font-bold text-[#1B2A4E] mb-4">Solicitud Recibida</h2>
          <p className="text-stone-500 text-lg leading-relaxed mb-8">
            Tu solicitud fue enviada y está pendiente de revisión por nuestro equipo de moderadores. Te notificaremos por email cuando sea aprobada.
          </p>
          <Link 
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-600 font-bold py-4 px-8 rounded-2xl transition-colors w-full"
          >
            <SafeIcon name="ArrowLeft" className="w-5 h-5" /> Volver al Inicio
          </Link>
        </motion.div>
      </div>
    );
  }

  // View: Invite path — account created, email confirmation required before login
  if (submitState === 'approved_success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#FAF9F6] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#74ACDF]/20 to-[#1B2A4E]/5"></div>
        <div className="absolute inset-0 opacity-5 pointer-events-none sol-watermark" style={{backgroundSize: '600px'}}></div>
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-[40px] shadow-2xl p-10 md:p-14 max-w-lg w-full text-center border border-stone-100 relative z-10"
        >
          <div className="w-24 h-24 bg-[#74ACDF]/15 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon name="Mail" className="w-12 h-12 text-[#74ACDF]" />
          </div>
          <h2 className="text-3xl font-editorial font-bold text-[#1B2A4E] mb-4">Confirmá tu email</h2>
          <p className="text-stone-500 text-lg leading-relaxed mb-8">
            Revisá tu email para confirmar tu cuenta y así completar tu ingreso a Argentina Emprende
            {inviter?.nombre ? ` (invitación de ${inviter.nombre})` : ''}.
          </p>
          <Link 
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-600 font-bold py-4 px-8 rounded-2xl transition-colors w-full"
          >
            <SafeIcon name="ArrowLeft" className="w-5 h-5" /> Volver al Inicio
          </Link>
        </motion.div>
      </div>
    );
  }

  // View: Form
  return (
    <div className="min-h-screen bg-[#FAF9F6] py-12 px-4 relative">
      <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none sol-watermark" style={{backgroundSize: '1000px', backgroundPosition: 'top center'}}></div>
      
      <div className="max-w-3xl mx-auto relative z-10">
        <Link to={inviteCode ? `/invitacion/${inviteCode}` : "/"} className="inline-flex items-center gap-2 text-stone-400 hover:text-[#1B2A4E] transition-colors font-medium mb-8">
          <SafeIcon name="ArrowLeft" className="w-5 h-5" /> Volver
        </Link>

        <div className="bg-white rounded-[40px] shadow-2xl border border-stone-100 overflow-hidden">
          {/* Header */}
          <div className="bg-[#1B2A4E] p-10 md:p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#74ACDF] rounded-full blur-[80px] opacity-20 -mr-20 -mt-20"></div>
            <div className="relative z-10">
              {inviteCode && inviter ? (
                <div className="flex items-center gap-4 mb-6">
                  <img src={inviter.foto_url} alt="" className="w-12 h-12 rounded-full border-2 border-white shadow-md" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#F6B40E] font-bold">Invitación Especial</p>
                    <p className="text-sm text-white/80">Vienes de parte de {inviter.nombre}</p>
                  </div>
                </div>
              ) : (
                <img
                  src={SITE_LOGO_URL}
                  alt="Argentina Emprende"
                  className="w-14 h-14 rounded-2xl object-cover mb-6 shadow-md"
                />
              )}
              <h1 className="text-4xl md:text-5xl font-editorial font-bold mb-3">
                Sumate al <span className="text-[#74ACDF] italic">Movimiento</span>
              </h1>
              <p className="text-white/80 text-lg">
                {inviteCode 
                  ? "Completá tus datos para acceder directamente a la red."
                  : "Completá tu perfil para solicitar acceso a la comunidad privada."}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 md:p-12 space-y-12">
            
            {/* 1. Datos Personales */}
            <section>
              <h3 className="text-xl font-editorial font-bold text-[#1B2A4E] mb-6 flex items-center gap-3 border-b border-stone-100 pb-4">
                <div className="w-8 h-8 rounded-full bg-[#74ACDF]/10 flex items-center justify-center text-[#74ACDF]">1</div>
                Datos Personales
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Nombre y Apellido</label>
                  <input required type="text" className="w-full px-5 py-4 rounded-2xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]" placeholder="Ej: Lionel Messi" value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} />
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Email Profesional</label>
                  <input required type="email" className="w-full px-5 py-4 rounded-2xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]" placeholder="tu@empresa.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Contraseña</label>
                  <input required type="password" minLength="6" className="w-full px-5 py-4 rounded-2xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]" placeholder="••••••••" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Foto de Perfil (URL Opcional)</label>
                  <input type="url" className="w-full px-5 py-4 rounded-2xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E] text-sm" placeholder="https://..." value={formData.foto_url} onChange={e => setFormData({...formData, foto_url: e.target.value})} />
                </div>
                <div className="md:col-span-2">
                  <label className="block label-technical text-stone-500 mb-3">Tu conexión con Argentina</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { value: 'soy_argentino', label: 'Soy argentino/a' },
                      { value: 'familiar_argentino', label: 'Familiar de argentino/a' },
                      { value: 'fan_de_argentina', label: 'Fan de Argentina' },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center gap-3 cursor-pointer rounded-2xl border px-4 py-4 transition-colors ${
                          formData.conexion_argentina === option.value
                            ? 'border-[#74ACDF] bg-[#74ACDF]/10 text-[#1B2A4E]'
                            : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-stone-300'
                        }`}
                      >
                        <input
                          required
                          type="radio"
                          name="conexion_argentina"
                          className="sr-only"
                          checked={formData.conexion_argentina === option.value}
                          onChange={() => setFormData({ ...formData, conexion_argentina: option.value })}
                        />
                        <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                          formData.conexion_argentina === option.value
                            ? 'border-[#74ACDF] bg-[#74ACDF]'
                            : 'border-stone-300 bg-white'
                        }`} />
                        <span className="text-sm font-medium">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* 2. Negocio / Especialidad */}
            <section>
              <h3 className="text-xl font-editorial font-bold text-[#1B2A4E] mb-6 flex items-center gap-3 border-b border-stone-100 pb-4">
                <div className="w-8 h-8 rounded-full bg-[#F6B40E]/10 flex items-center justify-center text-[#F6B40E]">2</div>
                Tu Negocio o Especialidad
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Ciudad / Capítulo</label>
                  <select required className="w-full px-5 py-4 rounded-2xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]" value={formData.capitulo_id} onChange={e => setFormData({...formData, capitulo_id: e.target.value})}>
                    <option value="" disabled>Seleccioná tu base</option>
                    {capitulos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Rubro Principal</label>
                  <select required className="w-full px-5 py-4 rounded-2xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]" value={formData.rubro} onChange={e => setFormData({...formData, rubro: e.target.value})}>
                    {RUBROS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <label className="block label-technical text-stone-500 mb-2">¿Qué ofreces a la red? (Especialidad)</label>
                  <textarea required rows="2" className="w-full px-5 py-4 rounded-2xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]" placeholder="Ej: Desarrollo de software a medida y consultoría en transformación digital para PyMEs." value={formData.ofrezco} onChange={e => setFormData({...formData, ofrezco: e.target.value})} />
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">¿Qué estás buscando?</label>
                  <textarea required rows="2" className="w-full px-5 py-4 rounded-2xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]" placeholder="Ej: Contactos en Real Estate que necesiten automatizar sus procesos de ventas." value={formData.busco} onChange={e => setFormData({...formData, busco: e.target.value})} />
                </div>
              </div>
            </section>

            {/* 3. Contacto / Privacidad */}
            <section>
              <h3 className="text-xl font-editorial font-bold text-[#1B2A4E] mb-6 flex items-center gap-3 border-b border-stone-100 pb-4">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">3</div>
                Contacto y Privacidad
              </h3>
              <div className="bg-stone-50 p-6 md:p-8 rounded-3xl border border-stone-200">
                <div className="mb-6">
                  <label className="block label-technical text-stone-500 mb-2">Número de WhatsApp (Con código de país)</label>
                  <input required type="tel" className="w-full max-w-md px-5 py-4 rounded-2xl bg-white border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]" placeholder="+1 305 555 0101" value={formData.whatsapp_number} onChange={e => setFormData({...formData, whatsapp_number: e.target.value})} />
                </div>
                
                <label className="flex items-start gap-4 cursor-pointer group">
                  <div className="relative flex items-center justify-center mt-1">
                    <input type="checkbox" className="sr-only" checked={formData.whatsapp_visible} onChange={e => setFormData({...formData, whatsapp_visible: e.target.checked})} />
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${formData.whatsapp_visible ? 'bg-[#25D366] border-[#25D366]' : 'border-stone-300 bg-white'}`}>
                      {formData.whatsapp_visible && <SafeIcon name="Check" className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                  <div>
                    <span className="block font-bold text-[#1B2A4E] mb-1 group-hover:text-[#74ACDF] transition-colors">Mostrar botón de WhatsApp en mi perfil público</span>
                    <span className="block text-sm text-stone-500 leading-relaxed">Permite que otros miembros aprobados te contacten directamente. Recomendado para hacer negocios más rápido.</span>
                  </div>
                </label>
              </div>
            </section>

            <div className="pt-6 border-t border-stone-100">
              {error && (
                <p className="text-red-600 text-sm font-medium bg-red-50 rounded-xl px-4 py-3 mb-4">{error}</p>
              )}
              <button 
                type="submit" 
                disabled={isLoading}
                className="w-full bg-[#1B2A4E] hover:bg-[#253969] text-white font-bold py-5 rounded-2xl transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 flex items-center justify-center gap-3 text-lg"
              >
                {isLoading ? (
                  <SafeIcon name="Loader" className="animate-spin w-6 h-6" />
                ) : (
                  <>
                    {inviteCode ? 'Crear Cuenta y Entrar' : 'Enviar Solicitud de Acceso'} 
                    <SafeIcon name="ArrowRight" className="w-5 h-5" />
                  </>
                )}
              </button>
              <p className="text-center text-xs text-stone-400 mt-4 label-technical">
                Al continuar aceptas los términos y condiciones de la comunidad.
              </p>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}